/**
 * 国旗 SVG：与 Flutter `assets/flags/{iso}.svg` 同源（lipis/flag-icons 4×3）。
 * 优先本地 /flags，CDN 作冷备；禁 emoji。
 */

export const FLAG_ICONS_VERSION = "7.3.2";
const CDN_4X3 = `https://cdn.jsdelivr.net/gh/lipis/flag-icons@${FLAG_ICONS_VERSION}/flags/4x3`;
const CDN_FALLBACK = `https://unpkg.com/flag-icons@${FLAG_ICONS_VERSION}/flags/4x3`;

/** 区域/组织伪码 → 资产文件名（与 mobile assets/flags 对齐） */
const SPECIAL: Record<string, string> = {
  eu: "eu",
  europe: "eu",
  un: "un",
  ww: "un",
  world: "un",
  global: "un",
  intl: "un",
  as: "as", // American Samoa；亚洲区域用 asean/gl 等
  asean: "asean",
  arab: "arab",
  gl: "gl",
};

export function normalizeFlagCode(code?: string | null): string | null {
  if (!code) return null;
  let c = code.trim().toLowerCase();
  if (!c) return null;
  if (c === "uk") c = "gb";
  if (SPECIAL[c]) return SPECIAL[c];
  // 2 位 ISO 或特殊 3+ 资产名（arab/asean）
  if (/^[a-z]{2}$/.test(c)) return c;
  if (/^[a-z]{3,8}$/.test(c) && SPECIAL[c]) return SPECIAL[c];
  return null;
}

/**
 * attempt: 0=本地 public/flags，1=jsDelivr，2=unpkg
 */
export function flagSvgUrl(code?: string | null, attempt: 0 | 1 | 2 = 0): string | null {
  const iso = normalizeFlagCode(code);
  if (!iso) return null;
  if (attempt === 0) return `/flags/${iso}.svg`;
  if (attempt === 1) return `${CDN_4X3}/${iso}.svg`;
  return `${CDN_FALLBACK}/${iso}.svg`;
}

/** 是否全球/区域（多国）套餐 — 用地球图标而非单国旗 */
export function isMultiGeo(geoType?: string | null, codes?: string[] | null): boolean {
  const t = (geoType || "").toUpperCase();
  if (t === "GLOBAL" || t === "REGION") return true;
  return (codes?.length || 0) > 1;
}

/** 套餐主国旗 code */
export function primaryFlagCode(
  geoCodes?: string[] | null,
  geoType?: string | null,
  countryHint?: string | null,
): string | null {
  if (countryHint) return normalizeFlagCode(countryHint) || countryHint;
  if (isMultiGeo(geoType, geoCodes)) return null;
  const codes = geoCodes || [];
  if (codes.length === 1) return codes[0];
  return null;
}

/** Intl 区域显示名（随语言切换，对齐 Flutter localizedCountryName） */
export function countryDisplayName(code: string, locale = "en"): string {
  const iso = (code || "").trim().toUpperCase();
  if (!iso) return "";
  if (iso === "GLOBAL" || iso === "WW") {
    return locale.startsWith("zh") ? "全球" : "Global";
  }
  try {
    const loc = locale.startsWith("zh") ? "zh-CN" : locale.split("-")[0] || "en";
    const name = new Intl.DisplayNames([loc], { type: "region" }).of(iso);
    if (name && name !== iso) return name;
  } catch {
    /* ignore */
  }
  return iso;
}
